import { Company, Employee, FeatureFlags } from "@finway-group/shared/lib/models"
import { notification } from "antd"
import Intercom from "Components/intercom"
import Loading from "Components/loading"
import { ConfirmEmail, ForgotPassword, Login, NewPassword, ResetPasswordContainer } from "Features/authentication"
import BugReport from "Features/errors/BugReport"
import ErrorPage from "Features/errors/ErrorPage"
import NotFoundPage from "Features/errors/NotFoundPage"
import { Reports } from "Features/pages/reporting/Reports.container"
import Workflows from "Features/pages/workflows/workflow.list"
import SettingsContainer from "Features/settings/settings.container"
import React, { Suspense, useEffect, useMemo } from "react"
import { useCookies } from "react-cookie"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Redirect, Switch, useHistory } from "react-router-dom"
import { isLocal } from "Shared/config/consts"
import { useCompanies } from "Shared/hooks/company.hooks"
import { useLoggedInEmployeeProfile, useLoggedInUserProfile } from "Shared/hooks/user.hooks"
import { NotificationService, UtilityService } from "Shared/services"
import { COMPANY_ID } from "Shared/services/localstorage.service"
import { logout } from "Shared/store/actions/auth/authActions"
import { RootState } from "Shared/store/rootState.interface"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import ScrollToTop from "Shared/utils/scrollToTop"
import { ErrorRoute } from "./ErrorRoute"
import { FullScreenRoute, PrivateFullScreen } from "./FullScreenRoute"
import PrivateRoute from "./PrivateRoute"
import { SelectedInboxInvoicesProvider } from "Shared/context/selectedExpenses.context"
import LiquidityContainer from "Features/pages/liquidity/liquidity.container"
import UserPilot from "Components/userPilot"
import { useFeatureFlags, useIsFreeVersion } from "Shared/hooks/featureFlags.hooks"
import { createCompanyIdCookieName } from "Shared/utils/cookie.utils"
import FileDownloadUrl from "Features/pages/files/fileDownloadUrl"

const CARD_BASE_ROUTE = `/credit-cards`
export const CARD_DETAILS_ROUTE = `${CARD_BASE_ROUTE}/:id`
export const EXPENSE_DETAILS_ROUTE = `/expenses/:id`

// lazy loading
const Dashboard = React.lazy(() => import("Features/pages/dashboard/dashboard"))
const ExpenseDetails = React.lazy(() => import("Features/pages/expenses/expenseDetails/expenseDetails.container"))

const CostCenters = React.lazy(() => import("Features/pages/costCenters/costCenter.list"))
const CostCenterDetail = React.lazy(() => import("Features/pages/costCenters/costCenterDetails/costCenterDetails.container"))

const Employees = React.lazy(() => import("Features/pages/employees/employees.list"))
const EmployeeDetail = React.lazy(() => import("Features/pages/employees/employeeDetails.container"))

const VendorList = React.lazy(() => import("Features/pages/vendors/vendor.list"))
const VendorDetail = React.lazy(() => import("Features/pages/vendors/vendorDetails.container"))

const SubscriptionList = React.lazy(() => import("Features/pages/subscriptions/subscription.list"))
const SubscriptionDetails = React.lazy(() => import("Features/pages/subscriptions/subscriptionDetails.container"))

const CreditCardContainer = React.lazy(() => import("Features/pages/creditCards/creditCard.container"))

const CreditCardDetail = React.lazy(() => import("Features/pages/creditCards/creditCardContainer/creditCardDetails.container"))
const WalletDetailsContainer = React.lazy(() => import("Features/pages/creditCards/walletDetails/walletDetails.container"))

const TransactionList = React.lazy(() => import("Features/pages/transactions/transactions.list"))
const TransactionsDetails = React.lazy(() => import("Features/pages/transactions/transactionsDetails.container"))

const AppRouter: React.FC = () => {
    const [cookies] = useCookies()
    const companies = useCompanies()
    const profile = useLoggedInUserProfile()
    const companyIdCookieName = createCompanyIdCookieName(profile)
    const cardsEnabled = companies.find((company: Company) => company._id === cookies[companyIdCookieName])?.cardsEnabled
    const isAuthenticated = useSelector(({ auth }: RootState) => auth.isAuthenticated)
    const user = new Employee(useLoggedInEmployeeProfile())
    const apiVersion = useSelector((state: RootState) => state.fetcher.apiVersion)
    const dispatch = useDispatch()
    const history = useHistory()
    const { t } = useTranslation()
    const isFreeVersion = useIsFreeVersion()
    const [canSee, setCanSee] = useStateIfMounted(true)
    const [checkedVersion, setCheckedVersion] = useStateIfMounted(false)
    const featuresFlags = useFeatureFlags()

    useEffect(() => {
        const checkApiVersion = async () => {
            const remoteApiVersion = await UtilityService.getApiVersion()
            if (apiVersion < remoteApiVersion) {
                notification.open({
                    type: "warning",
                    message: t("notification:new_api_version.title"),
                    description: t("notification:new_api_version.message"),
                    duration: 0,
                })

                await dispatch(logout())
                history.replace("/login")
            }
            setCheckedVersion(true)
        }

        !checkedVersion && checkApiVersion()
    }, [])

    useEffect(() => {
        if (isAuthenticated) {
            // Make sure to update the token by logging user out if any feature flag is missing
            if (
                featuresFlags &&
                (featuresFlags.paidVersion === undefined || featuresFlags.stripeEnabled === undefined || featuresFlags.outgoingWireTransferEnabled === undefined)
            ) {
                notification.open({
                    type: "info",
                    message: t("notification:new_api_version.title"),
                    description: t("notification:new_api_version.message"),
                    duration: 0,
                })
                dispatch(logout())
            }

            const activeProfile = user.setActiveCompanyProfile(cookies[companyIdCookieName])
            setCanSee(activeProfile?.isAdmin || activeProfile?.isApprover || false)
        }
    }, [user, cookies])

    const activeCompanyProfile = useMemo(() => {
        return user.setActiveCompanyProfile(cookies[companyIdCookieName])
    }, [user.activeCompanyProfile, cookies[companyIdCookieName]])

    return !checkedVersion ? (
        <Loading />
    ) : (
        <div>
            {!isLocal && <Intercom />}
            {!isLocal && <UserPilot />}
            <ScrollToTop />
            <Suspense fallback={<Loading />}>
                <Switch>
                    <FullScreenRoute exact path="/login">
                        <Login />
                    </FullScreenRoute>

                    <FullScreenRoute path="/forgot-password">
                        <ForgotPassword />
                    </FullScreenRoute>

                    <PrivateFullScreen path="/new-password">
                        <NewPassword />
                    </PrivateFullScreen>

                    <FullScreenRoute path="/reset/:token">
                        <ResetPasswordContainer />
                    </FullScreenRoute>

                    <FullScreenRoute path="/email/verify/:token">
                        <ConfirmEmail />
                    </FullScreenRoute>

                    <FullScreenRoute path="/bug-report">
                        <BugReport />
                    </FullScreenRoute>

                    <PrivateRoute path="/settings" includedFreeVersion>
                        <SettingsContainer />
                    </PrivateRoute>

                    <PrivateRoute exact path="/" includedFreeVersion>
                        <Redirect to={{ pathname: isFreeVersion ? "/liquidity" : "/expenses" }} />
                    </PrivateRoute>

                    <PrivateRoute exact path="/expenses/">
                        <Dashboard />
                    </PrivateRoute>

                    <PrivateRoute exact path="/expenses/:id">
                        <SelectedInboxInvoicesProvider>
                            <ExpenseDetails />
                        </SelectedInboxInvoicesProvider>
                    </PrivateRoute>

                    <PrivateRoute exact path="/subscriptions/">
                        <SubscriptionList />
                    </PrivateRoute>

                    <PrivateRoute exact path="/subscriptions/:id">
                        <SubscriptionDetails />
                    </PrivateRoute>

                    <PrivateRoute exact path={CARD_BASE_ROUTE} canSee={cardsEnabled}>
                        <CreditCardContainer />
                    </PrivateRoute>

                    <PrivateRoute exact path="/credit-cards/wallet-details" canSee={cardsEnabled}>
                        <WalletDetailsContainer />
                    </PrivateRoute>

                    <PrivateRoute exact path={CARD_DETAILS_ROUTE} canSee={cardsEnabled}>
                        <CreditCardDetail />
                    </PrivateRoute>

                    <PrivateRoute exact path="/transactions/" includedFreeVersion canSee={activeCompanyProfile?.isAdmin}>
                        <TransactionList />
                    </PrivateRoute>

                    <PrivateRoute exact path="/transactions/:id" includedFreeVersion>
                        <TransactionsDetails />
                    </PrivateRoute>

                    <PrivateRoute exact path="/workflows" canSee={activeCompanyProfile?.isAdmin}>
                        <Workflows />
                    </PrivateRoute>

                    <PrivateRoute exact path="/workflows/:id" canSee={activeCompanyProfile?.isAdmin}>
                        <Workflows />
                    </PrivateRoute>

                    <PrivateRoute exact path="/employees/" includedFreeVersion>
                        <Employees />
                    </PrivateRoute>

                    <PrivateRoute exact path="/employees/:id" includedFreeVersion>
                        <EmployeeDetail />
                    </PrivateRoute>

                    <PrivateRoute exact path="/vendors/">
                        <VendorList />
                    </PrivateRoute>
                    <PrivateRoute exact path="/vendors/:id">
                        <VendorDetail />
                    </PrivateRoute>

                    <PrivateRoute exact path="/costCenters/">
                        <CostCenters />
                    </PrivateRoute>

                    <PrivateRoute exact path="/costCenters/:id">
                        <CostCenterDetail />
                    </PrivateRoute>

                    <PrivateRoute path="/liquidity" includedFreeVersion canSee={activeCompanyProfile?.isAdmin}>
                        <LiquidityContainer />
                    </PrivateRoute>

                    <PrivateRoute exact path="/reports/" canSee={canSee}>
                        <Reports />
                    </PrivateRoute>

                    <PrivateRoute exact path="/files/download/url" canSee={canSee}>
                        <FileDownloadUrl />
                    </PrivateRoute>

                    <ErrorRoute path="/error">
                        <ErrorPage />
                    </ErrorRoute>

                    {/* this will redirect any URL without a route to "/not-found" */}
                    <ErrorRoute path="*">
                        <NotFoundPage />
                    </ErrorRoute>
                </Switch>
            </Suspense>
        </div>
    )
}

export default AppRouter
